"use client";

/**
 * The store behind `<ActivityFeed>` and `<Toaster>` (tdd-0079 §6).
 *
 * **One list, two views.** A toast is the transient projection of an item; the
 * feed is the durable one. They are not independent surfaces that happen to
 * show similar things — which is what makes dismissing a toast safe: nothing is
 * lost, the item is still in the feed.
 *
 * ## The merge rule
 *
 * An item with a `mergeKey` does not stack. A second `@mention` updates the
 * first in place and increments a count, so the sticky region is bounded no
 * matter how busy a review gets. Ten sticky toasts is most of a laptop
 * viewport, and the one thing a sticky element must guarantee is that it cannot
 * grow without limit.
 *
 * At this size a count is as actionable as a list anyway: the user's next move
 * is "go and look" either way, so show the newest — which has the most context —
 * and count the rest.
 */

import * as React from "react";

export type ActivityLevel = "info" | "action" | "urgent";

export interface ActivityItem {
  id: string;
  title: string;
  body?: string;
  level: ActivityLevel;
  /** Where acting on this takes the user. */
  href?: string;
  /**
   * Items sharing a key merge rather than stack, and the merged item shows the
   * newest plus a count.
   */
  mergeKey?: string;
  /**
   * Sticky items do not auto-dismiss. Reserved for things that need an
   * explicit acknowledgement — mentions, not saves.
   */
  sticky?: boolean;
  /** How many events this item represents. >1 only after a merge. */
  count: number;
  createdAt: number;
  /** Set when the item leaves the toast region. It stays in the feed. */
  dismissedAt: number | null;
  /** Set when the panel showed it. Drives the "things you missed" divider. */
  seenAt: number | null;
}

export type ActivityInput = Omit<
  ActivityItem,
  "id" | "count" | "createdAt" | "dismissedAt" | "seenAt"
>;

export interface ActivityContextValue {
  items: ActivityItem[];
  /** Items currently owed a toast — undismissed, newest last. */
  toasts: ActivityItem[];
  /** Unseen count, for a badge on the feed's own trigger. */
  unseen: number;
  /**
   * The boundary for "things you missed": the user's last active moment.
   * Items newer than this arrived while they were away.
   */
  missedSince: number | null;
  push: (input: ActivityInput) => string;
  dismiss: (id: string) => void;
  markAllSeen: () => void;
  clear: () => void;
}

/**
 * Apply one new item to the list.
 *
 * A pure function, and deliberately so: the merge rule is the behaviour §6
 * actually specifies, and the same logic inside a `setItems` callback needs a
 * rendered React tree to exercise. `packages/ui` has no testing-library, and
 * the precedent here (`useBorderChase`) is to keep the interesting arithmetic
 * in a function a test can call directly.
 */
export function applyActivity(
  prev: ActivityItem[],
  input: ActivityInput,
  ctx: { id: string; now: number; limit: number },
): ActivityItem[] {
  if (input.mergeKey) {
    const existing = prev.find(
      (i) => i.mergeKey === input.mergeKey && i.dismissedAt === null,
    );
    if (existing) {
      // The merged item shows the NEWEST content — it has the most context —
      // and counts what came before it. It does not stack, which is what keeps
      // the sticky region bounded however busy a review gets.
      return prev.map((i) =>
        i.id === existing.id
          ? {
              ...i,
              ...input,
              id: i.id,
              count: i.count + 1,
              createdAt: ctx.now,
              // A merge re-raises: the situation recurred, so an item the user
              // had already seen is news again.
              seenAt: null,
            }
          : i,
      );
    }
  }

  const item: ActivityItem = {
    ...input,
    id: ctx.id,
    count: 1,
    createdAt: ctx.now,
    dismissedAt: null,
    seenAt: null,
  };
  // Newest first, capped — the feed is a recent-activity view, not an archive.
  return [item, ...prev].slice(0, ctx.limit);
}

const ActivityContext = React.createContext<ActivityContextValue | null>(null);

/**
 * Read the activity store.
 *
 * Throws outside a provider rather than returning null: every consumer here is
 * inside an app that mounted one, and a silent no-op would present as
 * "notifications sometimes don't show" — the hardest class of bug to trace.
 */
export function useActivity(): ActivityContextValue {
  const ctx = React.useContext(ActivityContext);
  if (!ctx) {
    throw new Error("useActivity must be used within an <ActivityProvider>");
  }
  return ctx;
}

/** Non-throwing variant, for components that may render outside a provider. */
export function useActivityMaybe(): ActivityContextValue | null {
  return React.useContext(ActivityContext);
}

export interface ActivityProviderProps {
  children: React.ReactNode;
  /**
   * How long a non-sticky toast stays up (default 5000).
   *
   * A floor rather than a target: anything with an action in it should not
   * expire while it is still being read.
   */
  duration?: number;
  /**
   * Cap on retained items (default 100). The feed is a recent-activity view,
   * not an archive — the durable record is notifications-api's.
   */
  limit?: number;
  /**
   * The user's last active moment, for the "things you missed" divider.
   * Presence already records this, so it costs nothing to pass through.
   */
  missedSince?: number | null;
}

let nextId = 0;

export function ActivityProvider({
  children,
  duration = 5000,
  limit = 100,
  missedSince = null,
}: ActivityProviderProps) {
  const [items, setItems] = React.useState<ActivityItem[]>([]);

  const push = React.useCallback(
    (input: ActivityInput): string => {
      const now = Date.now();
      let resultId = "";

      setItems((prev) => {
        const merged = input.mergeKey
          ? prev.find((i) => i.mergeKey === input.mergeKey && i.dismissedAt === null)
          : undefined;
        resultId = merged?.id ?? `activity-${++nextId}`;
        return applyActivity(prev, input, { id: resultId, now, limit });
      });

      return resultId;
    },
    [limit],
  );

  const dismiss = React.useCallback((id: string) => {
    // Dismissal removes the TOAST, never the item. This is the whole reason the
    // two are one store: a user swatting a toast has not thrown anything away.
    setItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, dismissedAt: Date.now() } : i)),
    );
  }, []);

  const markAllSeen = React.useCallback(() => {
    const now = Date.now();
    setItems((prev) => prev.map((i) => (i.seenAt ? i : { ...i, seenAt: now })));
  }, []);

  const clear = React.useCallback(() => setItems([]), []);

  // Auto-dismiss non-sticky toasts.
  //
  // One timer per visible toast, keyed on identity, so a merge (which updates
  // `createdAt`) restarts the clock rather than letting the original expiry
  // fire on refreshed content.
  const visible = items.filter((i) => i.dismissedAt === null && !i.sticky);
  const visibleKey = visible.map((i) => `${i.id}:${i.createdAt}`).join(",");

  React.useEffect(() => {
    if (visible.length === 0) return;
    const timers = visible.map((item) =>
      setTimeout(() => dismiss(item.id), Math.max(0, item.createdAt + duration - Date.now())),
    );
    return () => timers.forEach(clearTimeout);
    // `visibleKey` is the identity of the visible set; depending on `visible`
    // itself would re-run on every render because `filter` returns a new array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleKey, duration, dismiss]);

  const value = React.useMemo<ActivityContextValue>(
    () => ({
      items,
      toasts: items.filter((i) => i.dismissedAt === null).slice().reverse(),
      unseen: items.filter((i) => i.seenAt === null).length,
      missedSince,
      push,
      dismiss,
      markAllSeen,
      clear,
    }),
    [items, missedSince, push, dismiss, markAllSeen, clear],
  );

  return <ActivityContext.Provider value={value}>{children}</ActivityContext.Provider>;
}
